// Adress-Cache fuer das Batch-Geocoding (ERP/CRM-Plan, Abschnitt 3.3).
//
// Nominatim erlaubt ~1 Anfrage/Sekunde. Damit derselbe Ort nicht zweimal
// erfragt wird, laeuft jede Verortung ueber diesen Cache — geschluesselt nach
// der NORMALISIERTEN Adresse, nicht nach der Eingabe: "Musterstr. 1" und
// "Musterstraße 1" sind derselbe Ort und muessen denselben Eintrag treffen.
//
// Die Normalisierung und der Hash sind pur und damit ohne Netz-/DOM-Umgebung
// pruefbar; nur die beiden Supabase-Helfer am Ende sind unrein.
// Tabelle + RLS: supabase/geo_foundation.sql (MIGRATIONS.md Nr. 13).

import { supabase } from './supabaseClient';

/**
 * Guete einer Verortung:
 * - 'exact'        : Hausnummer getroffen
 * - 'interpolated' : Strasse getroffen, Hausnummer geschaetzt/uebersprungen
 * - 'city'         : nur Ort/PLZ — fuer Umkreissuchen unbrauchbar
 * - 'failed'       : nichts gefunden; der Eintrag existiert nur fuer den Backoff
 */
export type GeoQuality = 'exact' | 'interpolated' | 'city' | 'failed';

/** Ein Cache-Eintrag in camelCase (Spalten in geo_foundation.sql). */
export interface GeoCacheEntry {
    id?: string;
    addressNorm: string;
    addressHash: string;
    lat?: number | null;
    lng?: number | null;
    provider?: string | null;
    quality?: GeoQuality | null;
    /** Anzahl bisheriger Versuche — traegt den Backoff bei 'failed'. */
    attempts: number;
    error?: string | null;
    /** Von Hand gesetzt: der Job fasst den Eintrag nicht an. */
    manual: boolean;
    resolvedAt?: string | null;
    createdAt?: string;
}

// ─── Normalisierung ─────────────────────────────────────────────────────────

// Umlaute und ß werden ausgeschrieben, damit "Straße"/"Strasse" und
// "Lübeck"/"Luebeck" zusammenfallen. Reihenfolge egal, die Muster ueberlappen
// sich nicht.
const UMLAUTE: [RegExp, string][] = [
    [/ä/g, 'ae'],
    [/ö/g, 'oe'],
    [/ü/g, 'ue'],
    [/ß/g, 'ss'],
];

/**
 * Ein Adressteil auf seine Vergleichsform bringen.
 *
 * Reihenfolge ist wesentlich: erst Umlaute (danach ist aus "straße" schon
 * "strasse"), dann die Abkuerzung "str"/"str." -> "strasse", erst danach
 * Satzzeichen entfernen — sonst waere der Punkt der Abkuerzung schon weg und
 * "Musterstr" nicht mehr von einem Wortende zu unterscheiden.
 */
const normalizePart = (raw?: string | null): string => {
    let s = (raw || '').toLowerCase();
    for (const [re, rep] of UMLAUTE) s = s.replace(re, rep);
    // "str", "str." -> "strasse", aber nur am Wortende bzw. vor der Hausnummer.
    // "strasse" selbst bleibt unberuehrt (dort folgt auf "str" ein "a").
    s = s.replace(/str\.?(?=[\s\d]|$)/g, 'strasse');
    // Satzzeichen trennen statt zu verschwinden: "musterweg,3" -> "musterweg 3".
    s = s.replace(/[.,;:/]+/g, ' ');
    // Zusammengeschriebene Hausnummer abtrennen: "musterstrasse1" -> "… 1".
    s = s.replace(/([a-z])(\d)/g, '$1 $2');
    return s.replace(/\s+/g, ' ').trim();
};

/**
 * Vergleichsform der vollstaendigen Adresse — zugleich die Zeichenkette, die
 * gehasht und (lesbar) in `geo_cache.address_norm` abgelegt wird.
 *
 * Leere Teile fallen weg, damit keine Fragmente wie ", 21079 hamburg"
 * entstehen (gleiche Regel wie nearbyProjects.buildAddressQuery). '' bedeutet:
 * keine verwertbare Adresse.
 *
 * Bewusst NICHT normalisiert: Bindestriche ("goethe-strasse" bleibt von
 * "goethestrasse" verschieden). Beides zusammenzuziehen wuerde verschiedene
 * Strassen verschmelzen — ein doppelter Cache-Eintrag ist der harmlosere
 * Fehler.
 */
export const normalizeAddress = (
    street?: string | null,
    zip?: string | null,
    city?: string | null,
): string => {
    const s = normalizePart(street);
    const z = normalizePart(zip);
    const c = normalizePart(city);
    return [s, [z, c].filter(Boolean).join(' ')].filter(Boolean).join(', ');
};

/**
 * Schluessel des Caches: 64 Bit als 16 Hex-Zeichen, gebildet aus zwei
 * unabhaengigen 32-Bit-Verfahren (djb2-xor und FNV-1a).
 *
 * Kein crypto: der Hash sichert nichts ab, er schluesselt nur einen Cache —
 * und crypto.subtle ist asynchron und nur im Secure Context verfuegbar (siehe
 * hash.ts), was eine pure Funktion unmoeglich machte. Ein einzelner 32-Bit-Wert
 * waere fuer einige tausend Adressen zu knapp (Geburtstagsproblem: bei 5.000
 * Adressen rund 0,3 % Kollisionswahrscheinlichkeit); mit 64 Bit liegt sie in
 * derselben Groessenordnung wie ein Plattenfehler.
 */
export const addressHash = (norm: string): string => {
    const s = norm || '';
    let djb2 = 5381;
    let fnv = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) {
        const c = s.charCodeAt(i);
        djb2 = ((djb2 * 33) ^ c) >>> 0;
        fnv = (fnv ^ c) >>> 0;
        // fnv * 16777619, ausmultipliziert — Math.imul waere gleichwertig,
        // Schiebeoperationen halten das Ergebnis sicher in 32 Bit.
        fnv = (fnv + ((fnv << 1) + (fnv << 4) + (fnv << 7) + (fnv << 8) + (fnv << 24))) >>> 0;
    }
    return djb2.toString(16).padStart(8, '0') + fnv.toString(16).padStart(8, '0');
};

/** Adresse -> {norm, hash} in einem Schritt; '' bei fehlender Adresse. */
export const addressKey = (
    street?: string | null,
    zip?: string | null,
    city?: string | null,
): { norm: string; hash: string } => {
    const norm = normalizeAddress(street, zip, city);
    return { norm, hash: norm ? addressHash(norm) : '' };
};

/** Anzeigetexte der Guete — die Oberflaeche zeigt nie den Rohwert. */
export const GEO_QUALITY_LABELS: Record<GeoQuality, string> = {
    exact: 'hausgenau',
    interpolated: 'straßengenau',
    city: 'nur Ortsmitte',
    failed: 'nicht auffindbar',
};

/** Guete lesbar; unbekannte/fehlende Werte ergeben ''. */
export const geoQualityLabel = (quality?: string | null): string =>
    quality && quality in GEO_QUALITY_LABELS ? GEO_QUALITY_LABELS[quality as GeoQuality] : '';

/** Koordinate deutsch formatiert ("53,5511 / 9,9937"). */
export const formatCoordinates = (lat?: number | null, lng?: number | null): string => {
    if (!Number.isFinite(lat as number) || !Number.isFinite(lng as number)) return '';
    const f = (n: number) => n.toFixed(4).replace('.', ',');
    return `${f(lat as number)} / ${f(lng as number)}`;
};

/** Brauchbare Koordinaten? 'failed'/'city'-Eintraege haben oft gar keine. */
export const hasCoordinates = (entry?: GeoCacheEntry | null): boolean =>
    !!entry && Number.isFinite(entry.lat as number) && Number.isFinite(entry.lng as number);

// ─── Supabase-Glue ──────────────────────────────────────────────────────────

const mapGeoCacheRow = (row: any): GeoCacheEntry => ({
    id: row.id,
    addressNorm: row.address_norm,
    addressHash: row.address_hash,
    lat: row.lat != null ? Number(row.lat) : null,
    lng: row.lng != null ? Number(row.lng) : null,
    provider: row.provider ?? null,
    quality: (row.quality ?? null) as GeoQuality | null,
    attempts: Number(row.attempts ?? 0),
    error: row.error ?? null,
    manual: !!row.manual,
    resolvedAt: row.resolved_at ?? null,
    createdAt: row.created_at,
});

const toGeoCacheRow = (e: GeoCacheEntry): Record<string, any> => ({
    address_norm: e.addressNorm,
    address_hash: e.addressHash,
    lat: e.lat ?? null,
    lng: e.lng ?? null,
    provider: e.provider ?? null,
    quality: e.quality ?? null,
    attempts: e.attempts ?? 0,
    error: e.error ?? null,
    manual: !!e.manual,
    resolved_at: e.resolvedAt ?? null,
});

/** Ein Eintrag ueber den Adress-Hash. null = noch nie verortet. */
export const lookupGeoCache = async (hash: string): Promise<GeoCacheEntry | null> => {
    if (!hash) return null;
    const { data, error } = await supabase
        .from('geo_cache')
        .select('*')
        .eq('address_hash', hash)
        .maybeSingle();
    if (error) throw error;
    return data ? mapGeoCacheRow(data) : null;
};

// PostgREST setzt die Filterliste in die URL — zu viele Hashes auf einmal
// sprengen die Zeilenlaenge des Servers. 200 ist die im Rechnungsmodul
// bewaehrte Groesse.
const IN_CHUNK = 200;

/**
 * Mehrere Eintraege auf einmal — der Backfill fragt seine gesamte
 * Kandidatenliste in wenigen Abfragen ab, statt je Kunde eine zu stellen.
 * Rueckgabe ist nach Hash geschluesselt; fehlende Hashes fehlen in der Map.
 */
export const lookupGeoCacheMany = async (hashes: string[]): Promise<Map<string, GeoCacheEntry>> => {
    const out = new Map<string, GeoCacheEntry>();
    const unique = Array.from(new Set((hashes || []).filter(Boolean)));
    for (let i = 0; i < unique.length; i += IN_CHUNK) {
        const chunk = unique.slice(i, i + IN_CHUNK);
        const { data, error } = await supabase.from('geo_cache').select('*').in('address_hash', chunk);
        if (error) throw error;
        for (const row of data || []) {
            const entry = mapGeoCacheRow(row);
            out.set(entry.addressHash, entry);
        }
    }
    return out;
};

/**
 * Eintrag anlegen oder fortschreiben (Konflikt auf `address_hash`).
 *
 * Von Hand gesetzte Eintraege (`manual`) sind tabu — der Aufrufer hat den
 * Cache ohnehin schon gelesen und erkennt sie dort; diese Pruefung ist der
 * zweite Riegel, damit ein kuenftiger Aufrufer sie nicht versehentlich
 * ueberschreibt. Rueckgabe: wurde geschrieben?
 */
export const upsertGeoCache = async (entry: GeoCacheEntry): Promise<boolean> => {
    if (!entry?.addressHash) return false;
    if (!entry.manual) {
        const bestehend = await lookupGeoCache(entry.addressHash);
        if (bestehend?.manual) return false;
    }
    const { error } = await supabase
        .from('geo_cache')
        .upsert(toGeoCacheRow(entry), { onConflict: 'address_hash' });
    if (error) throw error;
    return true;
};
